import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import { Shadows } from '../../../theme/Shadows.es.js';
import { Typography } from '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';
import { Link } from '../Link/Link.es.js';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty.es.js';
import { InteractiveTagProvider, InteractiveTagContext } from './InteractiveTagContext.es.js';
import { TooltipWrapper } from '../../display/TooltipWrapper/TooltipWrapper.es.js';

const Vars = createUniqueCustomProperties("InteractiveTagWrapper", [
  "backgroundColor",
  "backgroundColorHover",
  "backgroundColorFocus",
  "color",
  "colorHover",
  "colorFocus",
  "outlineColor",
  "outlineOpacity"
]);
const Styles = labelStyles("InteractiveTag", {
  root: (maxWidth) => css`
    max-width: ${maxWidth};
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
    padding: 11px 15px;
    height: 44px;
    border-radius: 30px;
    ${Typography.paragraph3Semibold};
    // Explicitly set 'line-height' to improve vertical alignment
    // in Safari.
    line-height: 44px;
    white-space: nowrap;
    transition: ${Animation.transitionValue};
    color: var(${Vars.color});
    background-color: var(${Vars.backgroundColor});
    text-decoration: none;

    &:hover {
      color: var(${Vars.colorHover});
      background-color: var(${Vars.backgroundColorHover});
      box-shadow: ${Shadows.elevation6};
    }

    &:focus {
      color: var(${Vars.colorFocus});
      background-color: var(${Vars.backgroundColorFocus});
      ${Vars.outlineOpacity}: 1;
      outline: none;
    }

    &[data-color='primary'] {
      border: 1px solid ${Colors.neutral600};
      ${Vars.color}: ${Colors.neutral800};
      ${Vars.colorHover}: ${Colors.blue};
      ${Vars.colorFocus}: ${Colors.blue};
      ${Vars.outlineColor}: ${Colors.blue};
      ${Vars.backgroundColor}: ${Colors.neutral200};
      ${Vars.backgroundColorHover}: ${Colors.neutral200};
      ${Vars.backgroundColorFocus}: ${Colors.neutral200};
      &:hover {
        border: 1px solid ${Colors.blue};
      }
      &:focus {
        border: none;
      }
    }

    &[data-color='secondary'] {
      ${Vars.color}: ${Colors.neutral100};
      ${Vars.colorHover}: ${Colors.neutral100};
      ${Vars.colorFocus}: ${Colors.neutral100};
      ${Vars.backgroundColor}: ${Colors.neutral800};
      ${Vars.outlineColor}: ${Colors.neutral800};
      ${Vars.backgroundColorHover}: ${Colors.neutral700};
      ${Vars.backgroundColorFocus}: ${Colors.neutral800};
    }

    &::before {
      content: '';
      opacity: var(${Vars.outlineOpacity}, 0);
      position: absolute;
      top: -4px;
      right: -4px;
      bottom: -4px;
      left: -4px;
      border: 2px solid var(${Vars.outlineColor});
      border-radius: inherit;
      transition: inherit;
    }
  `
});
const InteractiveTagWrapper = ({ children, color, href, onClick, maxWidth = "none" }) => {
  const sharedProps = {
    css: Styles.root(maxWidth),
    "data-color": color,
    onClick
  };
  const WrappedChildren = () => {
    if (href) {
      return jsx(Link, Object.assign({}, sharedProps, { color: null, href, weight: null }), children);
    }
    return jsx("button", Object.assign({}, sharedProps, { type: "button", "data-zds": true }), children);
  };
  return jsx(
    InteractiveTagProvider,
    null,
    jsx(InteractiveTagContext.Consumer, null, (context) => {
      return (context === null || context === void 0 ? void 0 : context.isTruncated) ? jsx(TooltipWrapper, { allowMultilineTooltip: true, content: context.body ? `${context.title}: ${context.body}` : `${context.title}`, shouldCorrectPosition: true }, () => jsx(WrappedChildren, null)) : jsx(WrappedChildren, null);
    })
  );
};

export { InteractiveTagWrapper };
//# sourceMappingURL=InteractiveTagWrapper.es.js.map
